import React, { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "../i18n";
import "./ProfilePreview.css";

function getPhoto(matchedUser) {
  return (
    matchedUser?.mainPhotoUrl ||
    matchedUser?.photoUrls?.[0] ||
    matchedUser?.photoUrl ||
    ""
  );
}

function getInitial(username) {
  return username ? username.charAt(0).toUpperCase() : "?";
}

function ProfilePreview({
  matchedUser,
  children,
  className = "",
  triggerRef,
  isOpen: controlledIsOpen,
  onClose,
  hideTrigger = false
}) {
  const { t } = useI18n();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
  const setIsOpen = isControlled ? onClose : setInternalIsOpen;
  const titleId = useId();
  const dialogId = useId();
  const photo = useMemo(() => getPhoto(matchedUser), [matchedUser]);
  const username = matchedUser?.username || t("profilePreview.unknownUser");

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      {!hideTrigger && (
        <button
        type="button"
        ref={triggerRef}
        className={`profile-preview-trigger ${className}`.trim()}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setIsOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
          }
        }}
        aria-label={`${t("profilePreview.viewProfile")}: ${username}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls={isOpen ? dialogId : undefined}
      >
        {children}
      </button>
      )}

      {isOpen &&
        createPortal(
          <div
            className="profile-preview-backdrop"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setIsOpen(false);
              }
            }}
          >
            <section
              id={dialogId}
              className="profile-preview-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="profile-preview-header">
                <h2 id={titleId}>{username}</h2>
                <button
                  type="button"
                  className="profile-preview-close"
                  onClick={() => setIsOpen(false)}
                  aria-label={t("profilePreview.close")}
                  autoFocus
                >
                  ×
                </button>
              </div>

              <div className="profile-preview-content">
                <div className="profile-preview-photo">
                  {photo ? (
                    <img
                      src={photo}
                      alt={username}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : (
                    <span>{getInitial(matchedUser?.username)}</span>
                  )}
                </div>

                <div className="profile-preview-details">
                  <p className="profile-preview-status">
                    <span
                      className={
                        matchedUser?.online === true
                          ? "profile-preview-status-dot online"
                          : "profile-preview-status-dot"
                      }
                    />
                    {matchedUser?.online === true
                      ? t("profilePreview.online")
                      : t("profilePreview.offline")}
                  </p>
                  <dl>
                    <div>
                      <dt>{t("profilePreview.age")}</dt>
                      <dd>{matchedUser?.age ?? t("profilePreview.notProvided")}</dd>
                    </div>
                    <div>
                      <dt>{t("profilePreview.gender")}</dt>
                      <dd>{matchedUser?.gender || t("profilePreview.notProvided")}</dd>
                    </div>
                    <div>
                      <dt>{t("profilePreview.location")}</dt>
                      <dd>
                        {[matchedUser?.city, matchedUser?.country]
                          .filter(Boolean)
                          .join(", ") || t("profilePreview.notProvided")}
                      </dd>
                    </div>
                  </dl>
                  <div className="profile-preview-bio">
                    <h3>{t("profilePreview.bio")}</h3>
                    <p>
                      {matchedUser?.bio?.trim() || t("profilePreview.bioMissing")}
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>,
          document.body
        )}
    </>
  );
}

export default ProfilePreview;
